<script lang="ts">
  import { keyBy, partition } from 'lodash-es'
  import { untrack } from 'svelte'
  import Flash, { type FlashState } from '#app/lib/components/flash.svelte'
  import Link from '#app/lib/components/link.svelte'
  import { getTextDirection } from '#app/lib/languages/languages'
  import { getLanguageLabel } from '#app/lib/languages/languages_labels'
  import { LogError } from '#app/lib/loggers'
  import { treq } from '#app/lib/treq'
  import Spinner from '#components/spinner.svelte'
  import type { SerializedEntity } from '#entities/lib/entities'
  import { entityDataShouldBeRefreshed } from '#entities/lib/entity_refresh'
  import type { ExternalSummaryInfo } from '#server/controllers/data/lib/summaries/getters'
  import type { WikipediaSummaryInfo } from '#server/controllers/data/lib/summaries/sitelinks'
  import type { SummaryInfo } from '#server/controllers/data/summaries'
  import type { ControllersAPI } from '#server/types/controllers_api'
  import { getCurrentLang, i18n } from '#user/lib/i18n'

  const {
    entity,
    showLabel = false,
  }: {
    entity: SerializedEntity
    showLabel?: boolean
  } = $props()

  type EnrichedSummaryInfo = ExternalSummaryInfo | (WikipediaSummaryInfo & { text?: string })
  type GetSummariesResponse = ControllersAPI['data']['get']['summaries']['response']
  type GetWpExtractResponse = ControllersAPI['data']['get']['wp-extract']['response']

  const uri = $derived(entity.uri)
  const originalLang = $derived(entity.originalLang)
  let summaryInfo: EnrichedSummaryInfo = $state()
  let summaries: EnrichedSummaryInfo[] = $state()
  let highlightedSummaries: EnrichedSummaryInfo[] = $state()
  let otherSummaries: EnrichedSummaryInfo[] = $state()
  let summeriesPerKey: Record<string, EnrichedSummaryInfo> = $state()
  let flash: FlashState = $state()
  let selectedSummary: string = $state()
  let waitingForSummariesData: Promise<GetSummariesResponse> = $state()
  let waitingForText: Promise<GetWpExtractResponse> = $state()

  const userLang = getCurrentLang()
  let langLabel: string = $state()
  getLanguageLabel(userLang)
    .then(label => langLabel = label)
    .catch(LogError)

  function getSummaries () {
    const refresh = entityDataShouldBeRefreshed(uri)
    if (summaries == null || refresh) {
      const langs = [ userLang ]
      if (originalLang && originalLang !== userLang) langs.push(originalLang)
      if (!langs.includes('en')) langs.push('en')
      waitingForSummariesData = treq('data', 'get', 'summaries', { uri, langs, refresh })
        .then((res: GetSummariesResponse) => {
          summaries = sortWikipediaSummaryFirst(res.summaries as SummaryInfo[])
          summeriesPerKey = keyBy(summaries, 'key')

          if (!selectedSummary) {
            [ highlightedSummaries, otherSummaries ] = partition(summaries, isHighlightedSummary)
            const bestOption = highlightedSummaries[0] || otherSummaries[0]
            if (bestOption) selectedSummary = bestOption.key
          }
        })
        .catch(err => flash = err)
    }
  }

  function sortWikipediaSummaryFirst (rawSummaries: SummaryInfo[]) {
    return rawSummaries.toSorted((a, b) => {
      if (a.key.includes('wiki')) return -1
      if (b.key.includes('wiki')) return 1
      return a.key > b.key ? -1 : 1
    })
  }

  const isHighlightedSummary = (summary: SummaryInfo) => summary.lang === userLang

  function resetSummaries () {
    summaries = null
    getSummaries()
  }

  $effect(() => {
    // This will trigger the first call to getSummaries, and further calls if the entity changes
    if (uri) untrack(resetSummaries)
  })

  function getSummaryText ({ key, lang, sitelink }: WikipediaSummaryInfo) {
    const { title } = sitelink
    waitingForText = treq('data', 'get', 'wp-extract', { lang, title })
      .then((res: GetWpExtractResponse) => {
        // @ts-expect-error
        summeriesPerKey[key].text = res.extract as string
        // Force refresh
        summaryInfo = summeriesPerKey[selectedSummary]
      })
      .catch(err => flash = err)
  }

  function getText () {
    if (summeriesPerKey) {
      summaryInfo = summeriesPerKey[selectedSummary]
      if ('sitelink' in summaryInfo) getSummaryText(summaryInfo)
    }
  }
  $effect(() => {
    if (selectedSummary) untrack(getText)
  })
  const splitOptions = $derived(highlightedSummaries?.length > 0 && otherSummaries?.length > 0)
</script>

<div class="summary" class:has-summary={summaries?.length > 0}>
  {#await waitingForSummariesData}
    <Spinner />
    {i18n('Looking for summaries…')}
  {:then}
    {#if summaries?.length > 0}
      <!-- The label 'summary' is not adapted to human or publisher entities -->
      <!-- Maybe the text doesn't need a label, has a summary/presentation text is kind of what one would expect to find here? -->
      {#if showLabel}
        <span class="label">{i18n('Summary')}</span>
      {/if}
      {#if summaries?.length > 1}
        <div class="header">
          <select bind:value={selectedSummary} aria-controls="summary-text">
            {#if splitOptions}
              <optgroup label={langLabel}>
                {#each highlightedSummaries as summary (summary.key)}
                  <option value={summary.key}>{summary.name}</option>
                {/each}
              </optgroup>
              <optgroup label={i18n('Other languages')}>
                {#each otherSummaries as summary (summary.key)}
                  <option value={summary.key}>{summary.name}</option>
                {/each}
              </optgroup>
            {:else}
              {#each summaries as summary (summary.key)}
                <option value={summary.key}>{summary.name}</option>
              {/each}
            {/if}
          </select>
        </div>
      {/if}
      {#await waitingForText}
        <Spinner center={true} />
      {:then}
        {#if summaryInfo?.text}
          <p id="summary-text" lang={summaryInfo.lang} dir={getTextDirection(summaryInfo.lang)}>
            {summaryInfo.text}
            <span class="source">
              <Link
                url={summaryInfo.link}
                text={`${i18n('Source:')} ${summaryInfo.name}`}
                classNames="link"
              />
            </span>
          </p>
        {/if}
      {/await}
    {/if}
  {/await}

  <Flash state={flash} />
</div>

<style lang="scss">
  @import "general/scss/utils";
  .summary.has-summary{
    padding: 1em;
    @include radius;
    background-color: $off-white;
    :global(.spinner-centered){
      padding: 2em;
    }
  }
  .header{
    @include display-flex(row, baseline, space-between);
    margin-block-end: 0.5em;
  }
  .label{
    color: $label-grey;
  }
  select{
    max-inline-size: 15em;
  }
  #summary-text{
    max-block-size: 10em;
    overflow-y: auto;
    overflow-wrap: anywhere;
  }
  .source{
    margin-inline-start: 1em;
    :global(a){
      color: $grey;
      white-space: nowrap;
    }
  }
</style>
